import { useState } from "react";
import {
  CHECK_LABELS,
  type CommentArtifact,
  citationTone,
  hasUnconfirmedCitation,
  type CompareArtifact,
  type CompareChange,
  type FindingArtifact,
  type InsertArtifact,
  PENDING_CHANGE_WARNING,
  pidLabel,
} from "../chat/artifacts";
import type { CardStatus } from "../chat/controller";
import type { InsertRequest } from "../hands/types";
import { CardStatusLine, PendingWarning } from "./cardParts";
import { Markdown } from "./Markdown";

function Dismissed({ what, onRestore }: { what: string; onRestore: () => void }) {
  return (
    <article className="xcard xcard--dismissed">
      <span className="xcard__dismissed-text">{what} dismissed</span>
      <button type="button" className="link-btn" onClick={onRestore}>
        Undo
      </button>
    </article>
  );
}

export function CommentCard({
  comment,
  status,
  locked = false,
  onAdd,
  onDismiss,
  onRestore,
  onJump,
}: {
  comment: CommentArtifact;
  status: CardStatus;
  locked?: boolean;
  onAdd: (text: string) => void;
  onDismiss: () => void;
  onRestore: () => void;
  onJump: () => void;
}) {
  const [text, setText] = useState(comment.text);
  if (status.state === "dismissed") return <Dismissed what="Comment" onRestore={onRestore} />;
  const done = status.state === "done";
  const busy = status.state === "working" || locked;
  return (
    <article className={`xcard xcard--comment${done ? " is-done" : ""}`} aria-label="Suggested comment">
      <header className="xcard__head">
        <span className="xcard__kind">Comment</span>
        <button type="button" className="link-btn" onClick={onJump}>
          Show
        </button>
      </header>
      <p className="doc-text xcard__quote">{comment.matched_text || comment.quote}</p>
      {done ? (
        <p className="xcard__comment-text">{text}</p>
      ) : (
        <>
          <textarea
            className="xcard__textarea"
            aria-label="Comment text"
            value={text}
            rows={Math.min(6, Math.max(2, Math.ceil(text.length / 48)))}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
          />
          <div className="xcard__actions">
            <button type="button" className="btn btn--primary btn--sm" disabled={busy || !text.trim()} onClick={() => onAdd(text)}>
              {status.state === "working" ? "Adding…" : "Add comment"}
            </button>
            <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={onDismiss}>
              Dismiss
            </button>
          </div>
        </>
      )}
      <CardStatusLine status={status} />
    </article>
  );
}

const AT_LABEL: Record<InsertArtifact["at"], string> = {
  cursor: "at the cursor",
  after_pid: "after the paragraph",
  replace_selection: "in place of the selection",
};

export function InsertCard({
  insert,
  status,
  locked = false,
  onInsert,
  onDismiss,
  onRestore,
}: {
  insert: InsertArtifact;
  status: CardStatus;
  locked?: boolean;
  onInsert: (at: InsertRequest["at"]) => void;
  onDismiss: () => void;
  onRestore: () => void;
}) {
  if (status.state === "dismissed") return <Dismissed what="Draft" onRestore={onRestore} />;
  const done = status.state === "done";
  const busy = status.state === "working" || locked;
  // Citations not all confirmed: the draft goes in only on purpose (spec §5.2).
  const unconfirmed = hasUnconfirmedCitation(insert);
  const primary = unconfirmed
    ? "Insert anyway"
    : insert.at === "after_pid"
      ? `Insert after ${pidLabel(insert.pid)}`
      : insert.at === "replace_selection"
        ? "Replace selection"
        : "Insert at cursor";
  return (
    <article className={`xcard xcard--insert${done ? " is-done" : ""}`} aria-label="Drafted text">
      <header className="xcard__head">
        <span className="xcard__kind">Draft · {AT_LABEL[insert.at]}</span>
      </header>
      <Markdown className="doc-text paper md" text={insert.markdown} />
      {insert.citations.length ? (
        <ul className="cites" aria-label="Citations in this draft">
          {insert.citations.map((c, i) => (
            <li key={i} className={`cite cite--${citationTone(c.status)}`}>
              <span className="cite__text">{c.text}</span>
              <span className="cite__status">{c.status}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {unconfirmed && !done ? <p className="warn">Not every citation in this draft was confirmed. Check them before you rely on it.</p> : null}
      {insert.warnings.includes(PENDING_CHANGE_WARNING) && !done ? <PendingWarning authors={[]} /> : null}
      {!done ? (
        <div className="xcard__actions">
          <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => onInsert(insert.at)}>
            {status.state === "working" ? "Inserting…" : primary}
          </button>
          {insert.at !== "cursor" ? (
            <button type="button" className="btn btn--sm" disabled={busy} onClick={() => onInsert("cursor")}>
              {unconfirmed ? "Insert at cursor anyway" : "Insert at cursor"}
            </button>
          ) : null}
          <button type="button" className="btn btn--quiet btn--sm" disabled={busy} onClick={onDismiss}>
            Dismiss
          </button>
        </div>
      ) : null}
      <CardStatusLine status={status} />
    </article>
  );
}

export function FindingCard({
  finding,
  canAsk,
  onJump,
  onAskFix,
}: {
  finding: FindingArtifact;
  canAsk: boolean;
  onJump: (pid: string, quote?: string) => void;
  onAskFix: () => void;
}) {
  const shown = finding.pids.slice(0, 8);
  return (
    <article className="xcard xcard--finding" aria-label="Clean-up finding">
      <header className="xcard__head">
        <span className="xcard__kind">{CHECK_LABELS[finding.check] ?? "Check"}</span>
      </header>
      <p className="xcard__message">{finding.message}</p>
      {finding.evidence.length ? (
        <ul className="xcard__evidence">
          {finding.evidence.slice(0, 3).map((e, i) => (
            <li key={i} className="doc-text">
              {e}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="xcard__actions">
        {shown.map((pid) => (
          <button key={pid} type="button" className="pid-ref pid-ref--link" onClick={() => onJump(pid)}>
            {pidLabel(pid)}
          </button>
        ))}
        {finding.pids.length > shown.length ? <span className="small">+{finding.pids.length - shown.length} more</span> : null}
        <button type="button" className="btn btn--sm xcard__fix" disabled={!canAsk} onClick={onAskFix}>
          Ask Ella to fix
        </button>
      </div>
    </article>
  );
}

const CHANGE_LABEL: Record<CompareChange["kind"], string> = {
  modified: "Changed",
  added: "Added",
  removed: "Removed",
  moved: "Moved",
};

function CompareRow({ change, otherLabel, onJump }: { change: CompareChange; otherLabel: string; onJump: (pid: string) => void }) {
  const shown = change.pids.slice(0, 4);
  const notable = change.materiality !== "low" && change.why && change.why !== "wording only";
  return (
    <li className="cmp" data-kind={change.kind}>
      <div className="cmp__head">
        <span className={`cmp__kind cmp__kind--${change.kind}`}>{CHANGE_LABEL[change.kind]}</span>
        {change.clause ? <span className="cmp__clause">{change.clause}</span> : null}
      </div>
      {change.kind === "modified" ? (
        <div className="cmp__texts">
          <p className="doc-text cmp__text">
            <span className="cmp__label">Now</span>
            {change.open_text}
          </p>
          <p className="doc-text cmp__text cmp__text--was">
            <span className="cmp__label">Was</span>
            {change.other_text}
          </p>
        </div>
      ) : (
        <p className={`doc-text cmp__text${change.kind === "removed" ? " cmp__text--was" : ""}`}>
          {change.kind === "removed" ? change.other_text : change.open_text || change.other_text}
        </p>
      )}
      <div className="cmp__foot">
        {notable ? <span className="cmp__why">{change.why}</span> : null}
        {change.kind === "removed" ? (
          <span className="small">
            Only in {otherLabel}
            {change.other_where ? ` (${change.other_where.replace(/¶p(\d+)/g, "¶$1")})` : ""}
          </span>
        ) : null}
        {shown.map((pid) => (
          <button key={pid} type="button" className="pid-ref pid-ref--link" onClick={() => onJump(pid)} title="Show in the document">
            {pidLabel(pid)}
          </button>
        ))}
        {change.pids.length > shown.length ? <span className="small">+{change.pids.length - shown.length} more</span> : null}
      </div>
    </li>
  );
}

export function CompareCard({ compare, onJump }: { compare: CompareArtifact; onJump: (pid: string) => void }) {
  const otherLabel = compare.against.kind === "version" ? "the saved version" : "the other document";
  const n = compare.total;
  return (
    <article className="xcard xcard--compare" aria-label="Comparison">
      <header className="xcard__head">
        <span className="xcard__kind">
          Compared with “{compare.against.name}” · {n === 0 ? "no changes" : `${n} change${n === 1 ? "" : "s"}`}
        </span>
      </header>
      {compare.changes.length ? (
        <ol className="cmp-list">
          {compare.changes.map((change) => (
            <CompareRow key={change.change_id} change={change} otherLabel={otherLabel} onJump={onJump} />
          ))}
        </ol>
      ) : n === 0 ? (
        <p className="xcard__message">The wording matches clause for clause (as if pending tracked changes were accepted).</p>
      ) : null}
      {compare.truncated && compare.changes.length < n ? (
        <p className="note">
          Showing the {compare.changes.length} most important of {n}. Ask Ella about a section to see the rest.
        </p>
      ) : null}
      {compare.partial ? <p className="note">The comparison stopped early; later clauses were not compared.</p> : null}
    </article>
  );
}
